<template>
  <div id="outerContainerFooter" class="outerContainer">
    <div id="footerBtns" class="innerContainer">
      <div class="footer-links" :class="{ 'is-loading': isLoading }">
        <router-link class="visitor-btn" to="/">
          HOME
        </router-link>
        <router-link
          v-for="page in footerPages"
          :key="page.id"
          class="visitor-btn"
          :to="`/${page.slug}`"
        >
          {{ (page.title || '').toUpperCase() }}
        </router-link>
        <template v-if="instagram || facebook">
          <span class="footer-divider" aria-hidden="true" />
          <span class="footer-social__label">Follow me on</span>
          <div class="footer-social__icons">
            <a v-if="instagram" :href="instagram" target="_blank" rel="noopener noreferrer" class="footer-social__link" aria-label="Instagram">
              <InstagramIcon />
            </a>
            <a v-if="facebook" :href="facebook" target="_blank" rel="noopener noreferrer" class="footer-social__link" aria-label="Facebook">
              <FacebookIcon />
            </a>
          </div>
        </template>
      </div>
      <small v-if="copyrightHolder">&copy; Copyright {{ year }}, {{ copyrightHolder }}</small>
    </div>
  </div>
</template>

<script>
import { store, actions } from '@/store'
import { artistConfig as defaultConfig } from '@/config/artist.config'
import InstagramIcon from '@/assets/icons/instagram.vue'
import FacebookIcon from '@/assets/icons/facebook.vue'

export default {
  name: 'FooterNav',
  components: { InstagramIcon, FacebookIcon },
  props: {
    config: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      isLoading: true
    }
  },
  computed: {
    artistConfig() {
      return this.config || store.artistConfig || defaultConfig
    },
    copyrightHolder() {
      return this.artistConfig.site?.copyrightHolder || this.artistConfig.artist?.name || ''
    },
    year() {
      return this.artistConfig.site?.copyrightYear || new Date().getFullYear()
    },
    footerPages() {
      return store.customPages
        .filter(page => !page.isHidden && page.showInFooter === true && page.id !== '_home')
        .sort((a, b) => (a.position || 0) - (b.position || 0))
    },
    instagram() {
      return this.toSocialUrl(this.artistConfig.artist?.socialMedia?.instagram)
    },
    facebook() {
      return this.toSocialUrl(this.artistConfig.artist?.socialMedia?.facebook)
    }
  },
  async mounted() {
    if (store.customPages.length > 0) this.isLoading = false
    await actions.loadCustomPages({ navOnly: true })
    this.isLoading = false
  },
  methods: {
    toSocialUrl(raw) {
      if (!raw) return ''
      return this.isValidHttpUrl(raw) ? raw : ''
    }
  }
}
</script>

<style scoped>

#outerContainerFooter {
  border-top: 1px solid color-mix(in srgb, var(--color-text) 15%, transparent);
  background-color: var(--color-footer);
  min-height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 20px;
}

#footerBtns {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;

  a {
    color: var(--color-footer-text);
    text-decoration: none;
    padding: 6px 10px;
    letter-spacing: 1px;
    font-size: var(--font-sm, 14px);
    font-family: var(--font-primary, 'Open Sans', sans-serif);
  }

  small {
    color: var(--color-footer-text);
    padding: 4px 10px;
    letter-spacing: 1px;
    font-size: var(--font-xs, 12px);
  }
}

.footer-links {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 4px;
  transition: opacity 0.15s ease;

  &.is-loading {
    opacity: 0;
    pointer-events: none;
  }

  /* Admin previews always show the desktop page */
  @media (max-width: 768px) {
    &:where(:not(.preview-page *)) {
      flex-direction: column;
      gap: 2px;
    }
  }
}

.footer-divider {
  display: inline-block;
  width: 1px;
  height: 14px;
  background: color-mix(in srgb, var(--color-footer-text) 30%, transparent);
  margin: 0 6px;
  vertical-align: middle;

  @media (max-width: 768px) {
    &:where(:not(.preview-page *)) {
      width: 40px;
      height: 1px;
      margin: 10px 0;
    }
  }
}

.footer-social__label {
  color: var(--color-footer-text);
  font-size: var(--font-sm, 14px);
  font-family: var(--font-primary, 'Open Sans', sans-serif);
  letter-spacing: 1px;

  @media (max-width: 768px) {
    &:where(:not(.preview-page *)) {
      margin: 4px 0;
    }
  }
}

.footer-social__icons {
  display: flex;
  flex-direction: row;
  gap: 8px;
}

.footer-social__link {
  color: var(--color-footer-text);
  display: flex;
  align-items: center;
  padding: 8px;
}
</style>
